import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import {
  ROUTES_WITH_HEADER,
  ROUTE_ACCESS_DENIED,
  ROUTE_ARCHIVE,
  ROUTE_ERROR_LOADING_DATA,
  ROUTE_HOME,
  ROUTE_INBOX,
  ROUTE_NO_SUBSCRIPTION,
  ROUTE_ROOT,
  ROUTE_SCREENSAVER,
  ROUTE_SEARCH,
  ROUTE_SIGN_IN,
  ROUTE_TOO_MANY_USERS,
  ROUTE_TRIAL_END,
  ROUTE_TRIAL_WELCOME,
  ROUTE_TV_CHANNELS,
  ROUTE_VOD
} from 'common/config/constants';
import { IVideoPlayer } from 'common/reducers/videoPlayer';
import { getClassName } from 'common/utils';
import { getRootPath } from 'common/utils/helpers';
import { StoreState } from 'index';
import React, { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
// eslint-disable-next-line import/named
import { RouteObject, useLocation, useRoutes } from 'react-router';
import AccessDenied from './AccessDenied';
import Archive from './Archive';
import CategoryArchive from './CategoryArchive';
import ErrorLoadingData from './ErrorLoadingData';
import Home from './Home';
import Inbox from './Inbox';
import Launcher from './Launcher';
import Login from './Login';
import NoSubscription from './NoSubscription';
import RestrictedAccess from './RestrictedAccess';
import Screensaver from './Screensaver';
import Search from './Search';
import TrialEnd from './TrialEnd';
import TrialWelcome from './TrialWelcome';
import TVChannels from './TVChannels';
import VOD from './VOD';

export type TRoute = RouteObject & { title?: string };

export const routes: TRoute[] = [
  {
    path: ROUTE_ROOT,
    element: <Launcher />
  },
  {
    path: ROUTE_HOME,
    element: <Home />
  },
  {
    path: ROUTE_TV_CHANNELS,
    element: <TVChannels />
  },
  {
    path: ROUTE_ARCHIVE,
    element: <Archive />
  },
  {
    path: ROUTE_TRIAL_WELCOME,
    element: <TrialWelcome />
  },
  {
    path: ROUTE_TRIAL_END,
    element: <TrialEnd />
  },
  {
    path: `${ROUTE_ARCHIVE}/:category/:subcategory?`,
    element: <CategoryArchive />
  },
  {
    path: ROUTE_SIGN_IN,
    element: <Login />
  },
  {
    path: ROUTE_TOO_MANY_USERS,
    element: <RestrictedAccess />
  },
  {
    path: ROUTE_NO_SUBSCRIPTION,
    element: <NoSubscription />
  },
  {
    path: ROUTE_ACCESS_DENIED,
    element: <AccessDenied />
  },
  {
    path: ROUTE_ERROR_LOADING_DATA,
    element: <ErrorLoadingData />
  },
  {
    path: ROUTE_SEARCH,
    element: <Search />
  },
  {
    path: ROUTE_INBOX,
    element: <Inbox />
  },
  {
    path: ROUTE_SCREENSAVER,
    element: <Screensaver />
  },
  {
    path: ROUTE_VOD,
    element: <VOD />
  },
  {
    path: `${ROUTE_VOD}/:category/:subcategory?`,
    element: <CategoryArchive vod={true} />
  }
];

interface PagesProps {
  className?: string;
  onFocus?: VoidFunction;
  onBlur?: VoidFunction;
}

const Pages = function Pages(props: PagesProps) {
  const { ref, focusKey } = useFocusable({
    focusKey: 'PAGE_WRAPPER',
    autoRestoreFocus: true,
    onFocus: props.onFocus,
    onBlur: props.onBlur
  });
  const location = useLocation();
  const isPlayerOpenned = useSelector(
    (state: StoreState) => (state.videoPlayer as IVideoPlayer).video?.isFullscreen,
    shallowEqual
  );

  const menuIsShown = useMemo(() => {
    return ROUTES_WITH_HEADER.includes(getRootPath(location.pathname));
  }, [location.pathname]);

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        ref={ref}
        className={getClassName(`page-wrapper ${props.className}`, {
          'menu-shown': menuIsShown,
          // Hide elements below player
          'player-openned': isPlayerOpenned
        })}
      >
        {useRoutes(routes)}
      </div>
    </FocusContext.Provider>
  );
};

export default React.memo(Pages);
